import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Widget from './Widget';
import api from '../../api/api';

const STATUS_COLORS = {
  COMPLETED: 'bg-success',
  RUNNING: 'bg-blue-500 animate-pulse',
  START: 'bg-blue-400',
  ERROR: 'bg-destructive',
  CANCELLED: 'bg-gray-500',
};

const STATUS_LABELS = {
  COMPLETED: 'Done',
  RUNNING: 'Running',
  START: 'Starting',
  ERROR: 'Error',
  CANCELLED: 'Cancelled',
};

const AIExecutionsWidget = ({ hideTitle = false, limit = 8 }) => {
  const navigate = useNavigate();
  const [executions, setExecutions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get('/ai/expert/api/executions/', {
        params: { page_size: limit, ordering: '-created_at' },
      });
      // api interceptor unwraps .data, so response is the paginated object directly
      const list = res?.results ?? (Array.isArray(res) ? res : []);
      setExecutions(list);
    } catch (err) {
      console.error('AIExecutionsWidget fetch error:', err);
      setError('Failed to load executions');
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    fetchData();
    const iv = setInterval(fetchData, 30_000);
    return () => clearInterval(iv);
  }, [fetchData]);

  const timeAgo = (dateStr) => {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  const fmtCost = (v) =>
    v != null ? `$${Number(v).toFixed(4)}` : '—';

  return (
    <Widget title="Recent AI Executions" hideTitle={hideTitle} className="h-full">
      <div className="h-full flex flex-col overflow-hidden">
        {loading && !executions.length ? (
          <div className="flex-1 flex items-center justify-center text-textSecondary text-sm">Loading…</div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2">
            <span className="text-destructive text-sm">{error}</span>
            <button className="btn btn-neutral btn-xs" onClick={fetchData}>Retry</button>
          </div>
        ) : executions.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-sm text-textSecondary">
            No executions yet
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto">
              <ul className="divide-y divide-borderDefault">
                {executions.map((ex) => (
                  <li
                    key={ex.id}
                    className="px-3 py-2 hover:bg-secondary/10 cursor-pointer transition"
                    onClick={() => navigate(`/ai/systems/${ex.ai_system}`)}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span
                          className={`flex-shrink-0 w-2 h-2 rounded-full ${STATUS_COLORS[ex.status] || 'bg-gray-500'}`}
                          title={ex.status_display || ex.status}
                        />
                        <span className="text-sm font-medium text-textPrimary truncate">
                          {ex.system_name || `System #${ex.ai_system}`}
                        </span>
                      </div>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                        ex.status === 'COMPLETED' ? 'bg-success/20 text-success' :
                        ex.status === 'ERROR' ? 'bg-destructive/20 text-destructive' :
                        ex.status === 'RUNNING' ? 'bg-blue-500/20 text-blue-400' :
                        'bg-gray-500/20 text-gray-400'
                      }`}>
                        {STATUS_LABELS[ex.status] || ex.status}
                      </span>
                    </div>
                    <div className="mt-1 flex items-center gap-3 text-[10px] text-textSecondary">
                      <span>{ex.execution_type_display || ex.execution_type}</span>
                      {ex.total_cost != null && <span>{fmtCost(ex.total_cost)}</span>}
                      {ex.execution_time_ms != null && (
                        <span>{(ex.execution_time_ms / 1000).toFixed(1)}s</span>
                      )}
                      <span className="ml-auto">{timeAgo(ex.created_at)}</span>
                    </div>
                    {ex.actions_summary && typeof ex.actions_summary === 'object' && (() => {
                      // actions_summary can be: { total, failed, actions: [...] } or a flat map { action_name: count }
                      const summary = ex.actions_summary;
                      const actionsArr = Array.isArray(summary.actions) ? summary.actions : (Array.isArray(summary) ? summary : null);
                      if (actionsArr && actionsArr.length > 0) {
                        // Group actions by tool_name/type and count them
                        const counts = {};
                        actionsArr.forEach(a => {
                          const key = a?.tool_name || a?.action_type || a?.type || 'action';
                          counts[key] = (counts[key] || 0) + 1;
                        });
                        return (
                          <div className="mt-1 flex flex-wrap gap-1.5">
                            {typeof summary.total === 'number' && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded bg-backgroundBody text-textSecondary">
                                {summary.total} actions{typeof summary.failed === 'number' && summary.failed > 0 ? ` (${summary.failed} failed)` : ''}
                              </span>
                            )}
                            {Object.entries(counts).slice(0, 5).map(([name, cnt]) => (
                              <span key={name} className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                                {name.replace(/_/g, ' ')}{cnt > 1 ? ` ×${cnt}` : ''}
                              </span>
                            ))}
                          </div>
                        );
                      }
                      // Flat map: { action_name: count_number }
                      const entries = Object.entries(summary).filter(([k]) => k !== 'total' && k !== 'failed' && k !== 'actions');
                      if (entries.length === 0) return null;
                      return (
                        <div className="mt-1 flex flex-wrap gap-1.5">
                          {entries.slice(0, 5).map(([action, val]) => (
                            <span key={action} className="text-[9px] px-1.5 py-0.5 rounded bg-primary/10 text-primary">
                              {action.replace(/_/g, ' ')}{typeof val === 'number' ? ` ×${val}` : ''}
                            </span>
                          ))}
                        </div>
                      );
                    })()}
                  </li>
                ))}
              </ul>
            </div>
            <div className="px-3 py-2 border-t border-borderDefault">
              <button
                className="text-xs text-primary hover:underline"
                onClick={() => navigate('/ai')}
              >
                View All Executions →
              </button>
            </div>
          </>
        )}
      </div>
    </Widget>
  );
};

export default AIExecutionsWidget;
